import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Download, 
  Eye, 
  Package, 
  Truck, 
  CheckCircle, 
  XCircle, 
  Clock,
  Filter,
  RefreshCw,
  ExternalLink,
  FileText,
  Calendar,
  User,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  AlertCircle
} from 'lucide-react';
import Alert from '../Alert';
import ConfirmModal from '../ConfirmModal';
import { API_BASE_URL } from '../../config';

// Helper functions for status styling - moved outside components for sharing
const getStatusColor = (status) => {
  switch (status) {
    case 'requested': return 'bg-yellow-100 text-yellow-800';
    case 'accepted': return 'bg-blue-100 text-blue-800';
    case 'rejected': return 'bg-red-100 text-red-800';
    case 'packed': return 'bg-purple-100 text-purple-800';
    case 'shipped': return 'bg-indigo-100 text-indigo-800';
    case 'delivered': return 'bg-green-100 text-green-800';
    case 'cancelled': return 'bg-gray-100 text-gray-800';
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case '': return 'bg-yellow-100 text-yellow-800';
    case null: return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'requested': return <Clock className="h-4 w-4" />;
    case 'accepted': return <CheckCircle className="h-4 w-4" />;
    case 'rejected': return <XCircle className="h-4 w-4" />;
    case 'packed': return <Package className="h-4 w-4" />;
    case 'shipped': return <Truck className="h-4 w-4" />;
    case 'delivered': return <CheckCircle className="h-4 w-4" />;
    case 'cancelled': return <XCircle className="h-4 w-4" />;
    case 'pending': return <Clock className="h-4 w-4" />;
    case '': return <Clock className="h-4 w-4" />;
    case null: return <Clock className="h-4 w-4" />;
    default: return <Clock className="h-4 w-4" />;
  }
};

const Orders = () => {
  const [activeTab, setActiveTab] = useState('requested');
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [loadingTracking, setLoadingTracking] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchBy, setSearchBy] = useState('order_id');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [exportLoading, setExportLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [orderStatistics, setOrderStatistics] = useState({
    order_history: 0,
    confirmed_orders: 0,
    packed_orders: 0,
    pending_orders: 0,
    total_orders: 0
  });
  const [alert, setAlert] = useState({ show: false, type: 'info', title: '', message: '' });
  const [confirmModal, setConfirmModal] = useState({ 
    show: false, 
    title: '', 
    message: '', 
    onConfirm: null,
    type: 'warning'
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
    };
  };

  useEffect(() => {
    loadOrders();
    loadOrderStatistics();
  }, [activeTab]);

  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm, searchBy, statusFilter, dateFilter]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        tab: activeTab,
        search: searchTerm,
        search_by: searchBy,
        status_filter: statusFilter,
        date_filter: dateFilter
      });

      const response = await fetch(`${API_BASE_URL}/admin/orders?${params}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      } else {
        const errorText = await response.text();
        console.error('Failed to load orders. Status:', response.status);
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    }
    setLoading(false);
  };

  const loadOrderStatistics = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/orders/statistics`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setOrderStatistics(data.statistics);
        }
      }
    } catch (error) {
      console.error('Error loading order statistics:', error);
    }
  };

  const filterOrders = () => {
    let filtered = [...orders];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(order => {
        const searchValue = searchTerm.toLowerCase();
        switch (searchBy) {
          case 'order_id':
            return order.order_id.toLowerCase().includes(searchValue);
          case 'email':
            return (order.customer?.email || order.customer_email || '').toLowerCase().includes(searchValue);
          case 'phone':
            return (order.customer?.phone || order.customer_phone || '').includes(searchValue);
          case 'sku':
            return order.items?.some(item => 
              item.product_sku?.toLowerCase().includes(searchValue)
            );
          default:
            return true;
        }
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.order_status === statusFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const orderDate = new Date();
      
      switch (dateFilter) {
        case 'today':
          filtered = filtered.filter(order => {
            const orderDate = new Date(order.created_at);
            return orderDate.toDateString() === now.toDateString();
          });
          break;
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          filtered = filtered.filter(order => {
            const orderDate = new Date(order.created_at);
            return orderDate >= weekAgo;
          });
          break;
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          filtered = filtered.filter(order => {
            const orderDate = new Date(order.created_at);
            return orderDate >= monthAgo;
          });
          break;
      }
    }

    setFilteredOrders(filtered);
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    // Show custom confirmation modal
    setConfirmModal({
      show: true,
      title: 'Confirm Status Update',
      message: `Are you sure you want to change the order status to "${newStatus}"?`,
      type: 'warning',
      onConfirm: async () => {
        // Close confirmation modal
        setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'warning' });
        
        try {
          const response = await fetch(`${API_BASE_URL}/admin/orders/${orderId}/status`, {
            method: 'PUT',
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ status: newStatus })
          });

          if (response.ok) {
            // Update local state
            setOrders(prev => prev.map(order => 
              order.order_id === orderId 
                ? { ...order, order_status: newStatus }
                : order
            ));
            
            // Refresh order statistics
            loadOrderStatistics();
            
            // Update selected order if it's the one being updated
            if (selectedOrder && selectedOrder.order_id === orderId) {
              setSelectedOrder(prev => ({ ...prev, order_status: newStatus }));
            }

            // Show success message
            setAlert({
              show: true,
              type: 'success',
              title: 'Status Updated',
              message: `Order status successfully updated to "${newStatus}". Orders refreshed automatically.`
            });
            
            // Close the modal after successful update
            setShowOrderModal(false);
            setSelectedOrder(null);
            
            // Secret refresh: Reload all orders after successful status update
            setRefreshing(true);
            setTimeout(() => {
              loadOrders().finally(() => {
                setRefreshing(false);
              });
            }, 500); // Small delay to ensure the API has processed the update
          } else {
            const errorData = await response.json();
            setAlert({
              show: true,
              type: 'error',
              title: 'Update Failed',
              message: `Failed to update order status: ${errorData.error || 'Unknown error'}`
            });
          }
        } catch (error) {
          console.error('Error updating order status:', error);
          setAlert({
            show: true,
            type: 'error',
            title: 'Error',
            message: 'Error updating order status. Please try again.'
          });
        }
      }
    });
  };

  const handleTrackOrder = async (orderId) => {
    setLoadingTracking(true);
    setShowTrackingModal(true);
    setTrackingData(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/orders/${orderId}/track`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setTrackingData({
          orderId,
          ...data,
          order: orders.find(o => o.order_id === orderId)
          });
        } else {
        const order = orders.find(o => o.order_id === orderId);
        setTrackingData({
          orderId,
          success: false,
          error: 'Failed to get tracking information.',
          order: order
        });
      }
    } catch (error) {
      console.error('Error getting tracking info:', error);
      const order = orders.find(o => o.order_id === orderId);
      setTrackingData({
        orderId,
        success: false,
        error: 'Error getting tracking information.',
        order: order
      });
    } finally {
      setLoadingTracking(false);
    }
  };



  const exportOrders = async (format = 'csv') => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams({
        format: format,
        tab: activeTab
      });

      const response = await fetch(`${API_BASE_URL}/admin/orders/export?${params}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `orders_${activeTab}_${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Export failed:', response.statusText);
      }
    } catch (error) {
      console.error('Error exporting orders:', error);
    }
    setExportLoading(false);
  };



  const formatAddress = (address) => {
    if (!address) {
      return 'No address provided';
    }
    
    if (typeof address === 'string') {
      try {
        address = JSON.parse(address);
      } catch (e) {
        return address;
      }
    }
    
    if (!address || typeof address !== 'object') {
      return 'Invalid address format';
    }
    
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.postal_code) parts.push(address.postal_code);
    
    return parts.length > 0 ? parts.join(', ') : 'No address details';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTabOrders = () => {
    switch (activeTab) {
      case 'history':
        return filteredOrders.filter(order => 
          ['delivered', 'cancelled', 'refunded'].includes(order.order_status)
        );
      case 'pending':
        return filteredOrders.filter(order => 
          ['confirmed', 'processing', 'shipped'].includes(order.order_status)
        );
      case 'packed':
        return filteredOrders.filter(order => 
          order.order_status === 'packed'
        );
      case 'requested':
        return filteredOrders.filter(order => 
          order.order_status === 'requested'
        );
      default:
        return filteredOrders;
    }
  };

  const getNextStatusOptions = (currentStatus) => {
    switch (currentStatus) {
      case 'requested':
        return ['packed', 'cancelled'];
      case 'packed':
        return ['shipped', 'cancelled'];
      case 'shipped':
        return ['delivered', 'cancelled'];
      case 'delivered':
        return ['refunded'];
      case 'pending':
        return ['confirmed', 'cancelled'];
      case 'confirmed':
        return ['processing', 'cancelled'];
      case 'processing':
        return ['shipped', 'cancelled'];
      default:
        return [];
    }
  };

  return (
    <div className="space-y-6">
      {/* Alert Component */}
      <Alert
        show={alert.show}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ show: false, type: 'info', title: '', message: '' })}
        autoClose={true}
        autoCloseDelay={4000}
      />

      {/* Confirmation Modal */}
      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'warning' })}
        confirmText="Confirm"
        cancelText="Cancel"
      />
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Order Management</h1>
          <p className="text-gray-600">
            Manage and track all customer orders
            {refreshing && (
              <span className="ml-2 inline-flex items-center text-xs text-blue-600">
                <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                Refreshing...
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => exportOrders('csv')}
            disabled={exportLoading}
            className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {exportLoading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            <span>Export CSV</span>
          </button>
          <button
            onClick={() => exportOrders('pdf')}
            disabled={exportLoading}
            className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            <FileText className="h-4 w-4" />
            <span>Export PDF</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { 
              id: 'history', 
              label: 'Order History', 
              count: orderStatistics.order_history
            },
            { 
              id: 'pending', 
              label: 'Confirmed Orders', 
              count: orderStatistics.confirmed_orders
            },
            { 
              id: 'packed', 
              label: 'Packed Orders', 
              count: orderStatistics.packed_orders || 0
            },
            { 
              id: 'requested', 
              label: 'Pending Orders', 
              count: orderStatistics.pending_orders
            }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <select
              value={searchBy}
              onChange={(e) => setSearchBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="order_id">Order ID</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="sku">Product SKU</option>
            </select>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
              <option value="refunded">Refunded</option>
            </select>
            
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
            
            <button
              onClick={loadOrders}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getTabOrders().map((order) => (
                  <tr key={order.order_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {order.order_id}
                        </div>
                        <div className="text-sm text-gray-500">
                          {order.items?.length || 0} items
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {order.customer?.name || order.customer_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {order.customer?.email || order.customer_email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        ₹{order.total_amount?.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {order.payment_status}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.order_status)}`}>
                        {getStatusIcon(order.order_status)}
                        <span className="ml-1">{order.order_status || 'pending'}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(order.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowOrderModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                          <button
                            onClick={() => handleTrackOrder(order.order_id)}
                          className="text-green-600 hover:text-green-800 transition-colors"
                          title="Track Order"
                          >
                          <CheckCircle className="h-4 w-4 fill-green-600 text-green-600" />
                          </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {getTabOrders().length === 0 && (
              <div className="text-center py-12">
                <Package className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No orders found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm ? 'Try adjusting your search criteria.' : 'No orders in this category yet.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => {
            setShowOrderModal(false);
            setSelectedOrder(null);
          }}
          onStatusUpdate={handleStatusUpdate}
        />
      )}

      {/* Tracking Modal */}
      {showTrackingModal && (
        <TrackingModal
          trackingData={trackingData}
          loading={loadingTracking}
          onClose={() => {
            setShowTrackingModal(false);
            setTrackingData(null);
          }}
        />
      )}
    </div>
  );
};

// Order Details Modal Component
const OrderDetailsModal = ({ order, onClose, onStatusUpdate }) => {
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const handleStatusUpdate = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await onStatusUpdate(order.order_id, newStatus);
      // Don't close modal here - let parent component handle it
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getNextStatusOptions = (currentStatus) => {
    switch (currentStatus) {
      case 'requested':
        return ['packed', 'cancelled'];
      case 'packed':
        return ['shipped', 'cancelled'];
      case 'shipped':
        return ['delivered', 'cancelled'];
      case 'delivered':
        return ['refunded'];
      case 'pending':
        return ['confirmed', 'cancelled'];
      case 'confirmed':
        return ['processing', 'cancelled'];
      case 'processing':
        return ['shipped', 'cancelled'];
      default:
        return [];
    }
  };

  const formatAddress = (address) => {
    if (!address) {
      return 'No address provided';
    }
    
    if (typeof address === 'string') {
      try {
        address = JSON.parse(address);
      } catch (e) {
        return address;
      }
    }
    
    if (!address || typeof address !== 'object') {
      return 'Invalid address format';
    }
    
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.postal_code) parts.push(address.postal_code);
    
    return parts.length > 0 ? parts.join(', ') : 'No address details';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Order Details</h2>
            <p className="text-sm text-gray-600">{order.order_id}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Order Status */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Order Status</h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.order_status)}`}>
                  {getStatusIcon(order.order_status)}
                                          <span className="ml-1 capitalize">{order.order_status || 'pending'}</span>
                </span>
                {order.tracking_number && (
                      <span className="text-sm text-gray-600">
                        Tracking: {order.tracking_number}
                  </span>
                )}
              </div>
              
              {getNextStatusOptions(order.order_status).length > 0 && (
                <div className="flex space-x-2">
                  {getNextStatusOptions(order.order_status).map(status => (
                    <button
                      key={status}
                      onClick={() => handleStatusUpdate(status)}
                      disabled={updatingStatus}
                      className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                      Mark as {status}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Customer Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <User className="h-5 w-5 mr-2" />
                Customer Information
              </h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600">{order.customer?.name || order.customer_name}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600">{order.customer?.email || order.customer_email}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600">{order.customer?.phone || order.customer_phone}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <MapPin className="h-5 w-5 mr-2" />
                Shipping Address
              </h3>
              <div className="text-sm text-gray-600">
                {formatAddress(order.customer_address || order.customer?.address || order.shipping_address)}
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Order Items</h3>
            <div className="bg-gray-50 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {order.items?.map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div>
                          <div className="font-medium">{item.product_name}</div>
                          {/* Show selected variants */}
                          {item.selected_variants && Object.keys(item.selected_variants).length > 0 ? (
                            <div className="mt-1">
                              {Object.entries(item.selected_variants).map(([variantName, variantValue]) => (
                                <span 
                                  key={variantName}
                                  className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-1 mb-1 font-medium"
                                >
                                  {variantName}: {variantValue}
                                </span>
                              ))}
                            </div>
                          ) : item.variants && Object.keys(item.variants).length > 0 ? (
                            <div className="mt-1">
                              {Object.entries(item.variants).map(([variantName, variantValue]) => (
                                <span 
                                  key={variantName}
                                  className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-1 mb-1 font-medium"
                                >
                                  {variantName}: {variantValue}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-1 text-xs text-gray-500">
                              No variants selected
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{item.product_sku}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{item.quantity}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">₹{item.unit_price}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">₹{item.total_price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Subtotal:</span>
                  <span className="text-sm font-medium">₹{order.subtotal}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Tax:</span>
                  <span className="text-sm font-medium">₹{order.tax_amount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Shipping:</span>
                  <span className="text-sm font-medium">₹{order.shipping_amount}</span>
                </div>
                {order.discount_amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Discount:</span>
                    <span className="text-sm font-medium text-green-600">-₹{order.discount_amount}</span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2">
                  <span className="text-sm font-semibold">Total:</span>
                  <span className="text-sm font-semibold">₹{order.total_amount}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Payment Method:</span>
                  <span className="text-sm font-medium capitalize">{order.payment_method}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Payment Status:</span>
                  <span className={`text-sm font-medium ${
                    order.payment_status === 'completed' ? 'text-green-600' : 'text-yellow-600'
                  }`}>
                    {order.payment_status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Order Date:</span>
                  <span className="text-sm font-medium">{formatDate(order.created_at)}</span>
                </div>
                {order.notes && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex items-start">
                      <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-yellow-800">Notes</p>
                        <p className="text-sm text-yellow-700 mt-1">{order.notes}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Tracking Modal Component
const TrackingModal = ({ trackingData, loading, onClose }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Order Tracking</h2>
            <p className="text-sm text-gray-600">{trackingData?.orderId || 'Loading...'}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : trackingData ? (
            <>
              {/* Tracking Status */}
              {trackingData.order && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4">Order Status</h3>
                  <div className="flex items-center space-x-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(trackingData.order.order_status)}`}>
                      {getStatusIcon(trackingData.order.order_status)}
                      <span className="ml-1 capitalize">{trackingData.order.order_status || 'pending'}</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Tracking Information */}
              {(() => {
                // Check for tracking info in response or order object
                const awbCode = trackingData.awb_code || trackingData.order?.awb_code;
                const trackingUrl = trackingData.tracking_url || trackingData.order?.tracking_url;
                const trackingNumber = trackingData.tracking_number || trackingData.order?.tracking_number;
                const hasTracking = (trackingData.success && trackingUrl) || awbCode || trackingUrl || trackingNumber;

                if (hasTracking) {
                  return (
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold mb-4 text-green-900">Tracking Available</h3>
                        <div className="space-y-3">
                          {awbCode && (
                            <div>
                              <span className="text-sm font-medium text-gray-600">AWB Code: </span>
                              <span className="text-sm text-gray-900 font-mono">{awbCode}</span>
                            </div>
                          )}
                          {trackingNumber && !awbCode && (
                            <div>
                              <span className="text-sm font-medium text-gray-600">Tracking Number: </span>
                              <span className="text-sm text-gray-900 font-mono">{trackingNumber}</span>
                            </div>
                          )}
                          {trackingUrl && (
                            <div>
                              <a
                                href={trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                              >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                View Tracking Details
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                } else if (trackingData.manual_delivery) {
                  return (
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold mb-2 text-blue-900">Manual Delivery</h3>
                          <p className="text-sm text-blue-700">
                            This is a manual delivery order. No external tracking available.
                            {trackingData.order && (
                              <span className="block mt-2">Current Status: <strong>{trackingData.order.order_status || 'pending'}</strong></span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                } else if (trackingData.success === false) {
                  return (
                    <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold mb-2 text-yellow-900">Tracking Information</h3>
                          <p className="text-sm text-yellow-700 mb-3">
                            {trackingData.message || 'No external tracking available at this time.'}
                          </p>
                          {trackingData.order && (
                            <div className="mt-3">
                              <p className="text-sm text-gray-600">Order Status: <strong className="text-gray-900">{trackingData.order.order_status || 'pending'}</strong></p>
                              {trackingData.order.created_at && (
                                <p className="text-sm text-gray-600 mt-1">Order Date: <strong className="text-gray-900">{formatDate(trackingData.order.created_at)}</strong></p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                } else if (trackingData.error) {
                  return (
                    <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold mb-2 text-red-900">Error</h3>
                          <p className="text-sm text-red-700">{trackingData.error}</p>
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  // Show order status information when tracking is not available
                  return (
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                      <div className="flex items-start">
                        <Truck className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold mb-2 text-blue-900">Order Status Information</h3>
                          {trackingData.order && (
                            <div className="space-y-2">
                              <p className="text-sm text-gray-700">
                                <span className="font-medium">Current Status:</span>{' '}
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-1">
                                  {trackingData.order.order_status || 'pending'}
                                </span>
                              </p>
                              {trackingData.order.created_at && (
                                <p className="text-sm text-gray-600">
                                  <span className="font-medium">Order Date:</span>{' '}
                                  <span>{formatDate(trackingData.order.created_at)}</span>
                                </p>
                              )}
                              <p className="text-sm text-blue-700 mt-3">
                                {trackingData.order.order_status === 'packed' || trackingData.order.order_status === 'shipped' 
                                  ? 'Tracking information will be available once the order is shipped with a carrier.'
                                  : 'No external tracking available for this order. Monitor the order status for updates.'}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
              })()}

              {/* Additional Tracking Details */}
              {trackingData.tracking && typeof trackingData.tracking === 'object' && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4">Tracking Details</h3>
                  <div className="space-y-2">
                    {Object.entries(trackingData.tracking).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                        <span className="text-sm text-gray-900">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No tracking data</h3>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Orders;